import React, {
  useEffect,
  useState,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import { alpha, useTheme } from "@mui/material/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import { useMaterialUIController } from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import loggerFactory from "globals/logger/logger-factory";
import { application, mcx } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";
import { FixedSizeList } from "react-window";
import { setRTCEvent } from "context";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

/* =========================
   Virtualized List (MEMO SAFE)
========================= */

const LISTBOX_PADDING = 8;
const OuterElementContext = React.createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const renderRow = ({ data, index, style }) =>
  React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });

const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          height={
            Math.min(8 * itemSize, itemCount * itemSize) + 2 * LISTBOX_PADDING
          }
          width="100%"
          itemSize={itemSize}
          itemCount={itemCount}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemData={itemData}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

/* =========================
   MAIN COMPONENT
========================= */

function LiveGroupCall({ liveGroupCallOpen, setLiveGroupCallOpen }) {
  const theme = useTheme();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState("");
  const [userList, setUserList] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const  department =  AuthenticationService.getDepartmentList() || [];
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  /* ---------- fetch users ---------- */
  const fetchUserList = useCallback(async () => {
    try {
       const payload = {
        searchRequest: { },
        departmentList: department.map((dept) => dept.department),
      }
      const response = await application.post(`uc/phoneBook/listByDepartment`, payload);
      const allContacts = response.data.data || [];

      const validContacts = allContacts.filter(
        (c) => c?.designation || c?.name,
      );

      setUserList(validContacts);
    } catch (error) {
      loggerFactory.debug("User list failed", error);
    }
  }, []);

  useEffect(() => {
    if (liveGroupCallOpen) {
      setSelectedUsers([]);
      setError("");
      fetchUserList();
    }
  }, [liveGroupCallOpen, fetchUserList]);

  /* ---------- handlers ---------- */

  const handleCancel = useCallback(() => {
    setSelectedUsers([]);
    setError("");
    setLiveGroupCallOpen(false);
  }, [setLiveGroupCallOpen]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!selectedUsers?.length) {
        setError("At least one user must be selected");
        return;
      }

      const groupName = Date.now().toString();
      const participantsString = selectedUsers
        .map(({ offEXtn }) => offEXtn)
        .join(",");

      const payload = {
        conferenceNo: groupName,
        conferenceStatus: "ACTIVE",
        participants: participantsString,
        enableSimulation: false,
      };

      try {
        const { status } = await mcx.post("floor", payload);
        if (status !== 201) throw new Error("Group creation failed");

        window.dispatchEvent(
          new CustomEvent("LIVE_GROUP_CALL_CREATED", {
            detail: { groupName, participants: selectedUsers },
          }),
        );

        await mcx.post(`floor/joinDynamic/${groupName}/1`);

        setRTCEvent(dispatch, {
          value: "LIVE GROUP CALL ANNOUNCEMENT",
          type: "GroupCall",
          data: groupName,
        });

        setNotify({
          isOpen: true,
          status: "Live group call created successfully",
          type: "success",
        });

        handleCancel();
      } catch (error) {
        loggerFactory.error("Live group call error", error);

        setNotify({
          isOpen: true,
          status:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create live group call",
          type: "error",
        });
      }
    },
    [selectedUsers, dispatch, handleCancel],
  );

  /* ---------- memo icons ---------- */

  const icon = useMemo(() => <CheckBoxOutlineBlankIcon fontSize="small" />, []);
  const checkedIcon = useMemo(() => <CheckBoxIcon fontSize="small" />, []);

  /* ---------- UI ---------- */

  return (
    <>
      <Dialog
        open={!!liveGroupCallOpen}
        maxWidth="md"
        onClose={handleCancel}
        fullWidth
        PaperProps={{
          sx: {
            ...CTLDialogContent(theme, { darkMode }),
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            boxShadow: `0 16px 36px ${alpha(theme.palette.common.black, 0.24)}`,
          },
        }}
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={(theme) => ({
              ...CTLDialogContent(theme, { darkMode }),
              pb: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            })}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                width: "100%",
              }}
            >
              <Box>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "info.main" }}
                >
                  Create Live Group Call
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{ color: "text", mt: 0.35, display: "block" }}
                >
                  Select users and start a live conference.
                </MDTypography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <MDButton
                  type="button"
                  onClick={handleCancel}
                  color="error"
                  variant="gradient"
                  size="small"
                  sx={{ borderRadius: "999px", px: 2.2 }}
                >
                  Cancel
                </MDButton> */}
                <MDButton
                  type="submit"
                  color="success"
                  variant="gradient"
                  size="small"
                  sx={{ borderRadius: "999px", px: 2.8 }}
                >
                  Start Call
                </MDButton>
                <IconButton
                  onClick={handleCancel}
                  aria-label="Close"
                  size="medium"
                  sx={{
                    color: theme.palette.error.main,
                    borderRadius: 1.5,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.12),
                    },
                  }}
                >
                  <CloseIcon fontSize="medium" />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={(theme) => ({
              ...CTLDialogContent(theme, { darkMode }),
              pt: 2.5,
              pb: 1.5,
              display: "grid",
              gap: 1.5,
              overflow: "visible",
            })}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pt: 2.75,
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
              }}
            >
              <Autocomplete
                multiple
                options={userList}
                disableCloseOnSelect
                disablePortal={false}
                PopperProps={{
                  placement: "bottom-start",
                  modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
                  style: { zIndex: 2000 },
                }}
                open={isDropdownOpen}
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                ListboxComponent={ListboxComponent}
                isOptionEqualToValue={(o, v) => o?.offEXtn === v?.offEXtn}
                getOptionLabel={(o) => o?.name ?? o?.designation}
                value={selectedUsers}
                onChange={(_, v) => setSelectedUsers(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      style={{ marginRight: 8 }}
                    />
                    {option.designation || option.name || option.offEXtn}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add users"
                    placeholder="Search users..."
                    sx={{
                      "& .MuiInputLabel-root": {
                        px: 0.5,
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        transition: "all 0.25s ease",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: alpha(theme.palette.info.main, 0.35),
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: alpha(theme.palette.info.main, 0.6),
                        },
                        "&.Mui-focused": {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.info.main, 0.2)}`,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.info.main,
                          borderWidth: 1.2,
                        },
                      },
                    }}
                  />
                )}
                fullWidth
                sx={{ flex: 1 }}
              />
            </Box>

            <MDTypography variant="caption" sx={{ color: "text" }}>
              {selectedUsers.length} participant
              {selectedUsers.length === 1 ? "" : "s"} selected
            </MDTypography>

            {error && (
              <FormHelperText
                error
                sx={{
                  fontSize: "0.875rem",
                  mt: 0.5,
                }}
              >
                {error}
              </FormHelperText>
            )}
          </DialogContent>
        </form>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}

export default React.memo(LiveGroupCall);
